import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAppData } from '../../../../../../context/AppDataContext';
import { useAlert } from '../../../../../../context/AlertContext';
import CloseIcon from '@mui/icons-material/Close';

import inventoryServices from '../inventoryServices';
import SelectInventory from './SelectInventory';
import { ArrowRight } from '@mui/icons-material';
import inventoryTransferItemServices from '../inventoryTransferItemServices';

function InventoryTransferItemForm({ transferDetails, inventoryStockList, callBack }) {
    
    const { AppAlert } = useAlert();
    const { productList, getAppDataById, companyLocationDD, getDDDataById } = useAppData();
    const [selectedInventory, setSelectedInventory] = useState(null);
    const [item, setItem] = useState(null);
    const [stockList, setStockList] = useState(null);
    const [error, setError] = useState("");

    const getStockList = () => {
        if (!item?.id) return;
        inventoryServices.getBillingStock(transferDetails.companyId, item.id)
            .then(res => {
                setStockList(res.data);
                
                if (res.data && res.data.length === 1) {
                    setItem(prev => ({ ...prev, stockId: res.data[0].id }));
                }
            })
            .catch(error => {
                console.log(error);
            });
    };

    useEffect(() => {
        if (selectedInventory) {
            let _productDetails = getAppDataById(productList, selectedInventory.productId);
            let _item = {}; 
            _item['productId'] = selectedInventory.productId;
            _item['title'] = _productDetails?.title || "Unknown Product";
            _item['cost'] = _productDetails?.cost || 0; 
            _item['price'] = _productDetails?.price || 0; 
            _item['mrp'] = _productDetails?.mrp || 0; 

            _item['qty'] = 1; 
            _item['available'] = Number(selectedInventory.qty) || 0; // Fixed spelling from "availabe" to "available"
            
            setItem(_item);
            setError(""); 
        }
    }, [selectedInventory]);

    const handleQtyChange = (val) => {
        const inputQty = Number(val);
        setItem(prev => ({ ...prev, qty: val }));

        // Max quantity instant inline validation
        if (inputQty > item?.available) {
            setError(`Cannot exceed available stock limit (${item.available})`);
        } else if (inputQty <= 0) {
            setError("Quantity must be greater than 0");
        } else {
            setError("");
        }
    };

    const handleCostChange = (val) => {
        setItem(prev => ({ ...prev, cost: val }));
    };

    useEffect(() => {
        if (item?.billingType === 1) {
            getStockList();
        }
    }, [item?.id]);

    const addItem = () => {
        const targetQty = Number(item.qty);

        // Guard Check Validation prior to API call execution
        if (targetQty > item.available) {
            setError(`Validation Error: Maximum stock capacity is ${item.available}`);
            AppAlert(`Cannot transfer more than available stock (${item.available})`, "error");
            return;
        }

        if (targetQty <= 0 || !item.qty) {
            setError("Please enter a valid quantity");
            return;
        }

        const payload = {
            ...item,
            qty: targetQty,
            cost: Number(item.cost) || 0,
            transferId: transferDetails.id,
        };

        inventoryTransferItemServices.save(payload).then(res => {
            AppAlert(res.msg);
            callBack(); 
            setItem(null); 
            setSelectedInventory(null);
            setError("");
        }).catch(err => {
            AppAlert(err.msg || "Error adding item", "error");
        });
    };

    const handleClose = () => {
        setItem(null);
        setSelectedInventory(null);
        setError("");
    };

    return (
        <>
            <div className="card border-0 shadow-sm p-3 bg-white rounded">
                <label className="form-label fw-bold text-secondary small mb-2 text-uppercase">Search & Select Stock Item</label>
                <SelectInventory
                    options={inventoryStockList} 
                    selectedInventory={selectedInventory} 
                    setSelectedInventory={setSelectedInventory} 
                />
            </div>

            <Dialog open={item !== null} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle className="d-flex justify-content-between align-items-center bg-light py-3 border-bottom">
                    <div>
                        <h6 className="mb-0 fw-bold text-dark">Configure Stock Transfer Line</h6>
                        <span className="badge bg-secondary small mt-1">Item Allocation Configuration</span>
                    </div>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent className="pt-4">
                    {item && (
                        <div className="p-1">
                            {/* Product title header box */}
                            <div className="p-3 bg-light rounded border mb-4">
                                <small className="text-muted text-uppercase fw-semibold d-block tracking-wider mb-1">Selected Product</small>
                                <h5 className="text-dark fw-bold mb-0">{item.title}</h5>
                            </div>

                            <div className="row g-3">
                                {/* Quantity input group container */}
                                <div className="col-md-12">
                                    <label className="form-label fw-semibold text-secondary">Transfer Quantity</label>
                                    <div className="input-group input-group-lg">
                                        <input 
                                            className={`form-control fw-bold ${error ? 'is-invalid' : 'is-valid'}`}
                                            type='number' 
                                            value={item.qty} 
                                            onChange={(e) => handleQtyChange(e.target.value)}
                                            min={1}
                                        />
                                        <span className="input-group-text bg-white border-start-0 text-muted fw-semibold">
                                            / Max Available: <span className="text-primary ms-1 fw-bold">{item.available}</span>
                                        </span>
                                    </div>
                                    {error && <div className="text-danger small fw-semibold mt-1">⚠️ {error}</div>}
                                </div>

                                {/* Custom Price breakdown item info */}
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold text-secondary">Transfer Rate (Cost)</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">₹</span>
                                        <input 
                                            className='form-control fw-semibold' 
                                            type='number' 
                                            value={item.cost} 
                                            onChange={(e) => handleCostChange(e.target.value)} 
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>

                                {/* Form total dynamic output evaluation summary item */}
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold text-secondary">Calculated Sub-Total</label>
                                    <div className="form-control bg-light fw-bold text-dark text-end border-dashed">
                                        ₹{((Number(item.qty) || 0) * (Number(item.cost) || 0)).toFixed(2)}
                                    </div>
                                </div>
                            </div>

                            {/* Action footer execution navigation tray */}
                            <div className="d-flex justify-content-end align-items-center mt-4 pt-3 border-top">
                                <button 
                                    className="btn btn-light border me-2 px-3 fw-semibold text-muted" 
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className='btn btn-primary px-4 fw-bold shadow-sm d-flex align-items-center gap-1' 
                                    onClick={addItem}
                                    disabled={!!error || !item.qty || Number(item.qty) <= 0}
                                >
                                    Move to <ArrowRight fontSize="small"/> {getDDDataById(companyLocationDD, transferDetails.toLocationId)?.label}
                                </button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

export default InventoryTransferItemForm;